/* eslint-disable no-console */
/**
 * Canvas Debug Logger
 *
 * Collects debug logs and allows downloading them as a file.
 * Much easier to read than console output!
 *
 * Usage in browser console:
 *   window.canvasDebug.download()   - Download logs as JSON file
 *   window.canvasDebug.clear()      - Clear all logs
 *   window.canvasDebug.getLogs()    - Get logs array
 *   window.canvasDebug.getLastN(5)  - Get last 5 entries
 */

interface DebugEntry {
  timestamp: string;
  category: string;
  action: string;
  data: Record<string, unknown>;
}

class CanvasDebugLogger {
  private logs: DebugEntry[] = [];
  private maxEntries = 1000;
  private enabled = true;

  constructor() {
    // Expose to window for easy access in console
    if (typeof window !== 'undefined') {
      (window as unknown as { canvasDebug: CanvasDebugLogger }).canvasDebug =
        this;
      console.log(
        '🔍 Canvas Debug Logger initialized. Use window.canvasDebug.download() to save logs.'
      );
    }
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    console.log(`🔍 Canvas Debug Logger ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Log a debug entry
   */
  log(
    category: string,
    action: string,
    data: Record<string, unknown> = {}
  ): void {
    if (!this.enabled) return;

    const entry: DebugEntry = {
      timestamp: new Date().toISOString(),
      category,
      action,
      data,
    };

    this.logs.push(entry);

    // Keep log size manageable
    if (this.logs.length > this.maxEntries) {
      this.logs = this.logs.slice(-this.maxEntries);
    }

    // Also log to console with collapsed group
    console.groupCollapsed(`🔍 [${category}] ${action}`);
    console.log('Timestamp:', entry.timestamp);
    console.log('Data:', data);
    console.groupEnd();
  }

  /**
   * Clear all logs
   */
  clear(): void {
    this.logs = [];
    console.log('🔍 Debug logs cleared');
  }

  /**
   * Get all logs
   */
  getLogs(): DebugEntry[] {
    return [...this.logs];
  }

  /**
   * Get last N entries
   */
  getLastN(n: number): DebugEntry[] {
    return this.logs.slice(-n);
  }

  /**
   * Format logs as readable text
   */
  formatAsText(): string {
    const lines: string[] = [
      '═══════════════════════════════════════════════════════════════════',
      '  CANVAS DEBUG LOG',
      `  Generated: ${new Date().toISOString()}`,
      `  Total entries: ${this.logs.length}`,
      '═══════════════════════════════════════════════════════════════════',
      '',
    ];

    for (const entry of this.logs) {
      lines.push(`[${entry.timestamp}] [${entry.category}] ${entry.action}`);
      lines.push(JSON.stringify(entry.data, null, 2));
      lines.push(
        '───────────────────────────────────────────────────────────────────'
      );
    }

    return lines.join('\n');
  }

  /**
   * Download logs as a file
   */
  download(format: 'json' | 'txt' = 'txt'): void {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(this.logs, null, 2);
      filename = `canvas-debug-${timestamp}.json`;
      mimeType = 'application/json';
    } else {
      content = this.formatAsText();
      filename = `canvas-debug-${timestamp}.txt`;
      mimeType = 'text/plain';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`🔍 Downloaded ${this.logs.length} log entries to ${filename}`);
  }

  /**
   * Get summary of recent activity
   */
  getSummary(): Record<string, number> {
    const summary: Record<string, number> = {};
    for (const entry of this.logs) {
      const key = `${entry.category}:${entry.action}`;
      summary[key] = (summary[key] || 0) + 1;
    }
    return summary;
  }
}

// Create singleton instance
export const canvasDebug = new CanvasDebugLogger();

// Export type for window augmentation
declare global {
  interface Window {
    canvasDebug: CanvasDebugLogger;
  }
}
